import { useState } from "react";
import { motion } from "framer-motion";
import { Minus, Plus, Check } from "lucide-react";
import { Reveal, Eyebrow, SectionTitle } from "@/components/Reveal";
import { scrollToId } from "@/lib/api";

const TIERS = [
  { k: "pictures", t: "Pictures", p: 20, unit: "per picture", f: ["Graphic art & cover art", "Profile pictures", "Advertisement graphics", "Free redo if not satisfied"] },
  { k: "videos", t: "Videos", p: 40, unit: "per video", f: ["Image-to-video art", "Video edits", "Video advertisements", "Professional finish"], hot: true },
  { k: "websites", t: "Websites", p: 40, unit: "per website", f: ["Custom design for your business", "Mobile friendly", "Built to level up your brand", "Professional finish"] },
];

const Counter = ({ label, price, value, set, k }) => (
  <div className="flex items-center justify-between py-4 border-b border-[var(--line)]">
    <div>
      <div className="font-display text-lg">{label}</div>
      <div className="font-mono text-xs text-[var(--ink-dim)]">${price} each</div>
    </div>
    <div className="flex items-center gap-3">
      <button data-testid={`calc-${k}-minus`} onClick={() => set(Math.max(0, value - 1))} className="w-9 h-9 btn-ghost grid place-items-center"><Minus className="w-4 h-4" /></button>
      <span data-testid={`calc-${k}-count`} className="w-6 text-center font-mono">{value}</span>
      <button data-testid={`calc-${k}-plus`} onClick={() => set(value + 1)} className="w-9 h-9 btn-ghost grid place-items-center"><Plus className="w-4 h-4" /></button>
    </div>
  </div>
);

const Calculator = () => {
  const [pic, setPic] = useState(1);
  const [vid, setVid] = useState(0);
  const [web, setWeb] = useState(0);
  const total = pic * 20 + vid * 40 + web * 40;
  return (
    <div data-testid="pricing-calculator" className="border border-[var(--gold)]/40 bg-[var(--crypt)] p-7 sm:p-10 hex-clip">
      <div className="font-mono text-xs tracking-[0.3em] uppercase text-[var(--gold)]">Instant quote</div>
      <Counter k="pictures" label="Pictures" price={20} value={pic} set={setPic} />
      <Counter k="videos" label="Videos" price={40} value={vid} set={setVid} />
      <Counter k="websites" label="Websites" price={40} value={web} set={setWeb} />
      <div className="flex items-end justify-between mt-8">
        <span className="font-mono text-xs tracking-widest uppercase text-[var(--ink-dim)]">Total</span>
        <motion.span key={total} initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} data-testid="calc-total" className="font-display font-black text-5xl text-gilded">
          ${total}
        </motion.span>
      </div>
      <button data-testid="calc-commission-btn" onClick={() => scrollToId("contact")} className="btn-blood w-full mt-8 py-4 font-mono text-sm tracking-[0.2em] uppercase">
        Request this order
      </button>
    </div>
  );
};

export const Pricing = () => (
  <section id="pricing" data-testid="pricing-section" className="relative py-28 sm:py-40 bg-[var(--crypt)]/50 border-y border-[var(--line)]">
    <div className="max-w-[1400px] mx-auto px-5 sm:px-10">
      <Reveal>
        <Eyebrow n="02">Pricing</Eyebrow>
        <SectionTitle>Royal work. <span className="italic text-[var(--blood)] glow-blood">Honest</span> prices.</SectionTitle>
      </Reveal>
      <div className="mt-16 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 grid sm:grid-cols-3 gap-4">
          {TIERS.map((t, i) => (
            <Reveal key={t.k} delay={i * 0.08}>
              <div data-testid={`price-card-${t.k}`} className={`group h-full relative p-7 border transition-colors duration-500 ${t.hot ? "border-[var(--blood)] bg-[var(--blood-deep)]/30" : "border-[var(--line)] bg-[var(--void)] hover:border-[var(--gold)]/60"}`}>
                {t.hot && <span className="absolute -top-3 left-7 bg-[var(--blood)] px-2 py-1 font-mono text-[10px] tracking-[0.25em] uppercase">Most wanted</span>}
                <div className="font-mono text-xs tracking-[0.3em] uppercase text-[var(--gold)]">{t.t}</div>
                <div className="mt-6 flex items-baseline gap-2">
                  <span className="font-display font-black text-6xl">${t.p}</span>
                </div>
                <div className="font-mono text-xs text-[var(--ink-dim)] mt-1">{t.unit}</div>
                <ul className="mt-8 space-y-3">
                  {t.f.map((f) => (
                    <li key={f} className="flex gap-3 text-sm text-[var(--ink-dim)]"><Check className="w-4 h-4 text-[var(--gold)] shrink-0 mt-0.5" />{f}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2} className="lg:col-span-4"><Calculator /></Reveal>
      </div>
    </div>
  </section>
);
